import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, ShieldAlert, Layers } from 'lucide-react';
import { InteriorSlide, SlideHeader, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
export function Slide02ExecSummary({
  index
}: SlideProps) {
  const cards = [{
    icon: AlertCircle,
    title: 'The reach gap',
    text: "The content is good. The reach isn't. QR codes are working, but email isn't.",
    delay: 0.4
  }, {
    icon: ShieldAlert,
    title: 'The channel reality',
    text: 'WhatsApp is the channel, but compliance is a grey area. You need someone to navigate that.',
    delay: 0.55
  }, {
    icon: Layers,
    title: 'The bandwidth problem',
    text: 'Managing 4 brands across multiple countries. You need a partner, not a content vendor.',
    delay: 0.7
  }];
  return <InteriorSlide surface banner={<SlideBanner eyebrow="The Goal" data-id="element-58">
          You want someone who takes the wheel — builds the strategy, runs the
          campaigns, and brings you results. Not more work.
        </SlideBanner>} data-id="element-57">
      <SlideHeader eyebrow="We Heard You, Arnaud" data-id="element-59">
        You clearly articulated key challenges and needs that resonate deeply
        with our expertise
      </SlideHeader>

      <div className="flex-1 flex flex-col lg:flex-row items-center gap-12 lg:gap-16 max-w-6xl mx-auto w-full" data-id="element-60">
        {/* Left: Text & Cards */}
        <div className="flex-1 space-y-8 w-full" data-id="element-61">
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="text-[clamp(1.1rem,1.4vw,1.3rem)] text-[var(--muted)] leading-relaxed" data-id="element-62">
            During our conversation, you shared that HCPs in West Africa don't
            use professional email. You're managing Breast Cancer, MS,
            Ophthalmology, and Hemophilia. Each needs campaigns. Each needs
            localization. You don't have the bandwidth to run all of this — and
            you shouldn't have to.
          </motion.p>

          <div className="space-y-4" data-id="element-63">
            {cards.map((card, i) => {
            const Icon = card.icon;
            return <motion.div key={card.title} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: card.delay,
              duration: 0.6
            }} className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 flex items-start gap-4" data-id="element-64">
                  <div className="h-10 w-10 rounded-xl bg-[var(--surface)] flex items-center justify-center flex-shrink-0 text-[var(--brand)]" data-id="element-65">
                    <Icon className="h-5 w-5" data-id="element-66" />
                  </div>
                  <div data-id="element-67">
                    <h3 className="text-lg font-bold text-[var(--fg)] mb-1" data-id="element-68">
                      {card.title}
                    </h3>
                    <p className="text-[var(--muted)] text-sm leading-relaxed" data-id="element-69">
                      {card.text}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Right: Visual */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.4,
        duration: 0.8
      }} className="flex-1 w-full relative aspect-[4/5] max-w-md rounded-3xl overflow-hidden shadow-2xl border-8 border-white" data-id="element-70">
          <img src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=1200&q=80" alt="African doctor reviewing information on a mobile device" className="w-full h-full object-cover" data-id="element-71" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--brand)]/80 via-transparent to-transparent mix-blend-multiply" data-id="element-72" />
        </motion.div>
      </div>
    </InteriorSlide>;
}